// "+ New security" modal: create a security without leaving the current
// screen. Two callers: the asset-transaction booking form (companyId gives
// the opening context) and the Security full form's "New Security" button
// (no company context). The Issuer select always shows, defaulted to the
// opening company but editable — a security from one company can convert
// into that of another. Options come from the same all-accounts list the
// booking form's Company select uses. Core identity (name / type /
// valuation type / currency) plus the key commercial terms; valuation type
// defaults from the chosen security type
// (cfg.security_types.default_valuation_type_id).

import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Col, Input, InputNumber, Modal, Row, Select } from 'antd'
import DatePicker from '../../../core/components/DatePicker.tsx'
import dayjs from 'dayjs'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { store } from '../../../state_manager/store.ts'
import { getAccessToken } from '../../../core/auth/authService'
import { createSecurity } from '../services/assetTransactionsService.ts'
import { useAccountOptions } from '../hooks/useAccountOptions.ts'
import { useNumberFormat, percentToDecimal } from '../../../core/utils/inputNumberFormat.ts'

// House form ergonomics: Enter commits the field (blurs) instead of leaving
// the cursor trapped in the input.
const blurOnEnter = (e: React.KeyboardEvent) => {
  if (e.key === 'Enter') (e.target as HTMLElement).blur()
}

const DATE_FORMAT = 'MM/DD/YYYY'

interface AddSecurityModalProps {
  open: boolean
  companyId: string | null
  companyName?: string | null
  defaultCurrencyId?: string | null
  // securityName lets callers append the new option locally without a refetch
  onCreated: (securityId: string, securityName?: string) => void
  onCancel: () => void
}

export default function AddSecurityModal({
  open,
  companyId,
  companyName,
  defaultCurrencyId,
  onCreated,
  onCancel
}: AddSecurityModalProps) {
  const securityTypes = useSelector((state: any) => state.globalConfigs?.security_types ?? [])
  const valuationTypes = useSelector((state: any) => state.globalConfigs?.asset_valuation_types ?? [])
  const currencies = useSelector((state: any) => state.globalConfigs?.currencies ?? [])
  const valuationCapTypes = useSelector((state: any) => state.globalConfigs?.valuation_cap_types ?? [])
  const interestRateTypes = useSelector((state: any) => state.globalConfigs?.interest_rate_types ?? [])
  const compoundingFrequencies = useSelector(
    (state: any) => state.globalConfigs?.compounding_frequencies ?? []
  )
  const dayCountConventions = useSelector((state: any) => state.globalConfigs?.day_count_conventions ?? [])

  const currencyFmt = useNumberFormat('currency')
  const unitPriceFmt = useNumberFormat('unit_price')

  const [saving, setSaving] = useState(false)
  // Issuer select: all accounts, fetched on open and refreshed each time the
  // dropdown opens (newly created accounts appear without a reload).
  const [issuerId, setIssuerId] = useState<string | null>(null)
  const { options: issuerOptions, onOpenRefetch: onIssuerOpenRefetch } = useAccountOptions(open)
  const [name, setName] = useState('')
  const [typeId, setTypeId] = useState<number | null>(null)
  const [valuationTypeId, setValuationTypeId] = useState<number | null>(null)
  const [currencyId, setCurrencyId] = useState<string | null>(defaultCurrencyId ?? null)
  const [issuePrice, setIssuePrice] = useState<number | null>(null)
  const [nominalValue, setNominalValue] = useState<number | null>(null)
  const [interestRate, setInterestRate] = useState<number | null>(null)
  const [interestRateTypeId, setInterestRateTypeId] = useState<number | null>(null)
  const [compoundingFrequencyId, setCompoundingFrequencyId] = useState<number | null>(null)
  const [dayCountConventionId, setDayCountConventionId] = useState<number | null>(null)
  const [maturityDate, setMaturityDate] = useState<string | null>(null)
  const [maturityMonths, setMaturityMonths] = useState<number | null>(null)
  const [valuationCapTypeId, setValuationCapTypeId] = useState<number | null>(null)
  const [valuationCap, setValuationCap] = useState<number | null>(null)
  const [valuationFloor, setValuationFloor] = useState<number | null>(null)
  const [conversionDiscount, setConversionDiscount] = useState<number | null>(null)
  const [strikePrice, setStrikePrice] = useState<number | null>(null)

  // Same cfg-driven visibility as the Security full-form: flags on
  // cfg.security_types decide which key terms apply; a type with no flags
  // (Loan, Token, ...) keeps the full set. No term fields at all until a
  // type is chosen — the relevant set appears with the selection.
  const selType = securityTypes.find((t: any) => t.id === typeId)
  const typeChosen = selType != null
  const anyFlag = Boolean(
    selType &&
      (selType.is_convertible ||
        selType.uses_interest ||
        selType.uses_nominal ||
        selType.uses_strike ||
        selType.uses_issue_price ||
        selType.uses_maturity)
  )
  const showAll = typeChosen && !anyFlag
  const showConvertible = Boolean(selType?.is_convertible)
  const usesInterest = Boolean(selType?.uses_interest)
  const usesMaturity = Boolean(selType?.uses_maturity)
  const showIssuePrice = showAll || Boolean(selType?.uses_issue_price)
  const showNominal = showAll || Boolean(selType?.uses_nominal)
  const showInterest = showAll || usesInterest
  const showMaturity = showAll || usesMaturity
  const showDiscount = showAll || showConvertible
  const showStrike = showAll || Boolean(selType?.uses_strike)
  const capType = valuationCapTypes.find((v: any) => v.id === valuationCapTypeId)
  const showCap = showAll || (showConvertible && Boolean(capType?.has_cap))
  const showFloor = showConvertible && Boolean(capType?.has_floor)
  const irType = interestRateTypes.find((r: any) => r.id === interestRateTypeId)
  const showCompounding = usesInterest && Boolean(irType?.compounds)
  // Currency amounts carry the selected currency's symbol (booking-form idiom)
  const ccySymbol = currencies.find((c: any) => c.id === currencyId)?.symbol || '$'

  const securityTypeOptions = useMemo(
    () => securityTypes.map((t: any) => ({ value: t.id, label: t.label })),
    [securityTypes]
  )
  const valuationTypeOptions = useMemo(
    () => valuationTypes.map((v: any) => ({ value: v.id, label: v.label })),
    [valuationTypes]
  )
  const currencyOptions = useMemo(
    () => currencies.map((c: any) => ({ value: c.id, label: c.code })),
    [currencies]
  )

  // Each open (re)defaults the issuer to the opening company — editable
  // after that (cross-company conversions issue under another company).
  useEffect(() => {
    if (open) setIssuerId(companyId ?? null)
  }, [open, companyId])

  // Currency default chain (owner spec): the caller's context currency
  // (transaction currency for subscriptions/purchases, the converting-out
  // security's currency for conversions — the caller picks which to pass),
  // else the base currency.
  const baseCurrencyId = useMemo(
    () => currencies.find((c: any) => c.is_base)?.id ?? null,
    [currencies]
  )
  useEffect(() => {
    if (open) setCurrencyId(defaultCurrencyId ?? baseCurrencyId ?? null)
  }, [open, defaultCurrencyId, baseCurrencyId])

  const reset = () => {
    setIssuerId(companyId ?? null)
    setName('')
    setTypeId(null)
    setValuationTypeId(null)
    setCurrencyId(defaultCurrencyId ?? baseCurrencyId ?? null)
    setIssuePrice(null)
    setNominalValue(null)
    setInterestRate(null)
    setInterestRateTypeId(null)
    setCompoundingFrequencyId(null)
    setDayCountConventionId(null)
    setMaturityDate(null)
    setMaturityMonths(null)
    setValuationCapTypeId(null)
    setValuationCap(null)
    setValuationFloor(null)
    setConversionDiscount(null)
    setStrikePrice(null)
  }

  const handleOk = async () => {
    const issuer = issuerId
    if (!issuer) {
      showErrorToast('Issuer is required')
      return
    }
    if (!name.trim() || !typeId || !currencyId) {
      showErrorToast('Name, security type and currency are required')
      return
    }
    setSaving(true)
    try {
      const token = getAccessToken(store.getState().auth)
      const res = await createSecurity(
        {
          name: name.trim(),
          issuer,
          type_id: typeId,
          currency_id: currencyId,
          valuation_type_id: valuationTypeId,
          // terms hidden for the selected type send null (a type switch
          // after typing must not smuggle stale values onto the security)
          issue_price: showIssuePrice ? issuePrice : null,
          nominal_value: showNominal ? nominalValue : null,
          // entered as percentages, stored as decimals (0.05 = 5%)
          interest_rate: showInterest ? percentToDecimal(interestRate) : null,
          interest_rate_type_id: showAll || usesInterest ? interestRateTypeId : null,
          compounding_frequency_id: showAll || showCompounding ? compoundingFrequencyId : null,
          day_count_convention_id: showAll || usesInterest ? dayCountConventionId : null,
          maturity_date: showMaturity ? maturityDate : null,
          maturity_months: showAll || (usesMaturity && showConvertible) ? maturityMonths : null,
          valuation_cap_type_id: showAll || showConvertible ? valuationCapTypeId : null,
          valuation_cap: showCap ? valuationCap : null,
          valuation_floor: showAll || showFloor ? valuationFloor : null,
          conversion_discount: showDiscount ? percentToDecimal(conversionDiscount) : null,
          strike_price: showStrike ? strikePrice : null
        },
        token
      )
      if (res.error || !res.securityId) {
        showErrorToast(res.error || 'Failed to create security')
        return
      }
      toast.success('Security created')
      onCreated(res.securityId, name.trim())
      reset()
    } finally {
      setSaving(false)
    }
  }

  const field = (label: string, control: React.ReactNode) => (
    <div className='flex items-center gap-2 mb-2'>
      <div className='w-32 shrink-0 text-right text-gray-500 text-sm'>{label}</div>
      <div className='flex-1 min-w-0'>{control}</div>
    </div>
  )

  // The defaulted company renders by name even before the all-accounts list
  // arrives (the opening context supplies the label).
  const issuerSelectOptions =
    companyId && !issuerOptions.some((o) => o.value === companyId)
      ? [{ value: companyId, label: companyName || 'Company' }, ...issuerOptions]
      : issuerOptions

  return (
    <Modal
      title='New security'
      open={open}
      onOk={handleOk}
      okText='Create security'
      confirmLoading={saving}
      onCancel={() => {
        reset()
        onCancel()
      }}
      width={800}
      destroyOnClose
    >
      {field(
        'Issuer',
        <Select
          className='w-full'
          value={issuerId ?? undefined}
          options={issuerSelectOptions}
          placeholder='Select company...'
          showSearch
          optionFilterProp='label'
          onDropdownVisibleChange={onIssuerOpenRefetch}
          onChange={(v) => setIssuerId(v ?? null)}
        />
      )}
      {field('Name', <Input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={blurOnEnter} placeholder='e.g. Series A Preferred' />)}
      {field(
        'Security type',
        <Select
          className='w-full'
          value={typeId}
          options={securityTypeOptions}
          placeholder='Select type...'
          showSearch
          optionFilterProp='label'
          onChange={(v) => {
            setTypeId(v)
            const t = securityTypes.find((x: any) => x.id === v)
            if (t?.default_valuation_type_id != null) setValuationTypeId(t.default_valuation_type_id)
          }}
        />
      )}
      {/* Valuation type only makes sense once a security type is chosen —
          it defaults from the type and is rarely overridden. */}
      {typeChosen &&
        field(
          'Valuation type',
          <Select
            className='w-full'
            value={valuationTypeId}
            options={valuationTypeOptions}
            placeholder='Defaults from security type'
            allowClear
            onChange={(v) => setValuationTypeId(v ?? null)}
          />
        )}
      {field(
        'Currency',
        <Select
          className='w-full'
          value={currencyId}
          options={currencyOptions}
          placeholder='Currency...'
          showSearch
          optionFilterProp='label'
          onChange={(v) => setCurrencyId(v ?? null)}
        />
      )}
      {typeChosen && (
        <>
          <div className='text-gray-400 text-xs mt-3 mb-2'>Key terms (optional)</div>
          <Row gutter={8}>
            <Col span={12}>
              {showIssuePrice &&
                field('Issue price', <InputNumber className='w-full' value={issuePrice} prefix={ccySymbol} {...unitPriceFmt} onKeyDown={blurOnEnter} onChange={(v) => setIssuePrice(v == null ? null : Number(v))} />)}
              {showNominal &&
                field('Nominal value', <InputNumber className='w-full' value={nominalValue} prefix={ccySymbol} {...unitPriceFmt} onKeyDown={blurOnEnter} onChange={(v) => setNominalValue(v == null ? null : Number(v))} />)}
              {showInterest &&
                field('Interest rate %', <InputNumber className='w-full' value={interestRate} suffix='%' onKeyDown={blurOnEnter} onChange={(v) => setInterestRate(v == null ? null : Number(v))} />)}
              {usesInterest &&
                field(
                  'Interest rate type',
                  <Select
                    className='w-full'
                    value={interestRateTypeId}
                    allowClear
                    options={interestRateTypes.map((r: any) => ({ value: r.id, label: r.label }))}
                    onChange={(v) => setInterestRateTypeId(v ?? null)}
                  />
                )}
              {showCompounding &&
                field(
                  'Compounding',
                  <Select
                    className='w-full'
                    value={compoundingFrequencyId}
                    allowClear
                    options={compoundingFrequencies.filter((c: any) => !c.once_off).map((c: any) => ({ value: c.id, label: c.label }))}
                    onChange={(v) => setCompoundingFrequencyId(v ?? null)}
                  />
                )}
              {usesInterest &&
                field(
                  'Day count',
                  <Select
                    className='w-full'
                    value={dayCountConventionId}
                    allowClear
                    options={dayCountConventions.map((d: any) => ({ value: d.id, label: d.label }))}
                    onChange={(v) => setDayCountConventionId(v ?? null)}
                  />
                )}
              {showMaturity &&
                field(
                  'Maturity date',
                  <DatePicker
                    className='w-full'
                    value={maturityDate ? dayjs(maturityDate) : null}
                    format={DATE_FORMAT}
                    onChange={(d) => setMaturityDate(d ? d.format('YYYY-MM-DD') : null)}
                  />
                )}
              {usesMaturity && showConvertible &&
                field(
                  'Maturity (months)',
                  <InputNumber className='w-full' value={maturityMonths} min={0} precision={0} onKeyDown={blurOnEnter} onChange={(v) => setMaturityMonths(v == null ? null : Number(v))} />
                )}
            </Col>
            <Col span={12}>
              {showConvertible &&
                field(
                  'Valuation cap type',
                  <Select
                    className='w-full'
                    value={valuationCapTypeId}
                    allowClear
                    // id order IS the display order (hand-assigned cfg ids)
                    options={valuationCapTypes.map((v: any) => ({ value: v.id, label: v.label }))}
                    onChange={(v) => setValuationCapTypeId(v ?? null)}
                  />
                )}
              {showCap &&
                field('Valuation cap', <InputNumber className='w-full' value={valuationCap} prefix={ccySymbol} {...currencyFmt} onKeyDown={blurOnEnter} onChange={(v) => setValuationCap(v == null ? null : Number(v))} />)}
              {showFloor &&
                field('Valuation floor', <InputNumber className='w-full' value={valuationFloor} prefix={ccySymbol} {...currencyFmt} onKeyDown={blurOnEnter} onChange={(v) => setValuationFloor(v == null ? null : Number(v))} />)}
              {showDiscount &&
                field('Discount %', <InputNumber className='w-full' value={conversionDiscount} suffix='%' onKeyDown={blurOnEnter} onChange={(v) => setConversionDiscount(v == null ? null : Number(v))} />)}
              {showStrike &&
                field('Strike price', <InputNumber className='w-full' value={strikePrice} prefix={ccySymbol} {...unitPriceFmt} onKeyDown={blurOnEnter} onChange={(v) => setStrikePrice(v == null ? null : Number(v))} />)}
            </Col>
          </Row>
        </>
      )}
    </Modal>
  )
}
